import React, { useState, useRef, useEffect } from 'react';

/**
 * ColorPicker - Simple color picker with preset swatches and custom input
 */
export default function ColorPicker({
  value = '#1e40af',
  onChange,
  label,
  presets = []
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef(null);

  // Default preset colors
  const defaultPresets = [
    '#1e40af', // Blue
    '#0f766e', // Teal
    '#7c3aed', // Purple
    '#be123c', // Rose
    '#ea580c', // Orange
    '#16a34a', // Green
    '#0284c7', // Sky
    '#dc2626', // Red
    '#ca8a04', // Yellow
    '#4b5563', // Gray
    '#000000', // Black
    '#ffffff', // White
  ];

  const colorPresets = presets.length > 0 ? presets : defaultPresets;

  // Sync with external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (color) => {
    setInputValue(color);
    onChange(color);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Validate hex color
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleInputBlur = () => {
    // Ensure valid hex on blur
    if (!/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
      setInputValue(value);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}

      {/* Color preview button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 transition-colors"
      >
        <div
          className="w-6 h-6 rounded-md border border-gray-300 shadow-inner"
          style={{ backgroundColor: value }}
        />
        <span className="text-sm font-mono text-gray-600 flex-1 text-left">
          {value.toUpperCase()}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-4">
          {/* Preset swatches */}
          <div className="grid grid-cols-6 gap-2 mb-4">
            {colorPresets.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handlePresetClick(color)}
                className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                  value === color ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>

          {/* Custom color input */}
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={value}
              onChange={(e) => {
                setInputValue(e.target.value);
                onChange(e.target.value);
              }}
              className="w-10 h-10 rounded cursor-pointer border-0"
            />
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="#000000"
              className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ColorPickerInline - Compact inline color picker
 */
export function ColorPickerInline({ value, onChange, size = 'md' }) {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };

  return (
    <div className="relative inline-block">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${sizes[size]} rounded-lg cursor-pointer border border-gray-300 shadow-sm hover:border-gray-400`}
        style={{ padding: 0 }}
      />
    </div>
  );
}
